/**
 * Shared utility functions for the schema renderer.
 */

import type { Audience, FieldDef } from './types';

/**
 * Resolve the display label for a field given a formId and (optionally) the
 * viewing audience.
 *
 * When an `audience` is supplied AND the field carries an
 * `audienceLabels[audience].label`, that wording wins. Otherwise the legacy
 * fallback applies unchanged: formId-specific → "default" → canonical_name.
 *
 * Passing no audience (or an audience the field has no copy for) yields the
 * exact same result as the original single-argument resolveLabel.
 */
export function resolveLabel(field: FieldDef, formId: string, audience?: Audience): string {
  if (audience) {
    const audienceLabel = field.audienceLabels?.[audience]?.label;
    if (audienceLabel) return audienceLabel;
  }
  return field.labels[formId] ?? field.labels['default'] ?? field.canonical_name;
}

/**
 * Resolve the help/hint text for a field given the viewing audience.
 *
 * Prefers `audienceLabels[audience].help_text` when present, otherwise falls
 * back to the field's plain `help_text`. With no audience-specific copy the
 * result is identical to reading `field.help_text` directly, so callers that
 * have bespoke hint fallbacks (e.g. DateR's format hint) can keep them:
 * `resolveHelpText(field, audience) ?? '<their fallback>'`.
 */
export function resolveHelpText(field: FieldDef, audience?: Audience): string | undefined {
  if (audience) {
    const audienceHelp = field.audienceLabels?.[audience]?.help_text;
    if (audienceHelp) return audienceHelp;
  }
  return field.help_text;
}

/**
 * Derive the react-hook-form field name from a FieldDef and optional namePrefix.
 *
 * When a namePrefix is provided (i.e. we're inside a repeater entry), we use
 * the last segment of the canonical_name to avoid double-nesting. The full
 * canonical path is preserved for the top-level case.
 *
 * Examples:
 *   canonical: "applicant.passport.family_name", prefix: undefined
 *   → "applicant.passport.family_name"
 *
 *   canonical: "applicant.children[*].family_name", prefix: "applicant.children.0"
 *   → "applicant.children.0.family_name"
 */
export function deriveFieldName(field: FieldDef, namePrefix: string | undefined): string {
  if (!namePrefix) return field.canonical_name;
  // Strip [*] wildcard and take the last segment
  const cleanPath = field.canonical_name.replace(/\[\*\]/g, '');
  const parts = cleanPath.split('.').filter(Boolean);
  const lastSegment = parts[parts.length - 1];
  return `${namePrefix}.${lastSegment}`;
}

/**
 * Format an ISO date string for display in PDF mode.
 * Converts "1990-05-15" → "15 May 1990"
 * Handles partial dates: "1990-05" → "May 1990", "1990" → "1990"
 */
export function formatDateForDisplay(iso: string): string {
  if (!iso) return '';
  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const parts = iso.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    if (!isNaN(m) && m >= 1 && m <= 12 && !isNaN(d)) {
      return `${d} ${MONTHS[m - 1]} ${year}`;
    }
  }
  if (parts.length === 2) {
    const [year, month] = parts;
    const m = parseInt(month, 10);
    if (!isNaN(m) && m >= 1 && m <= 12) {
      return `${MONTHS[m - 1]} ${year}`;
    }
  }
  return iso;
}

/**
 * Extract all options_ref paths from a schema tree (for pre-loading).
 */
export function collectOptionsRefs(fields: FieldDef[]): string[] {
  const refs: string[] = [];
  for (const field of fields) {
    if (field.options_ref) refs.push(field.options_ref);
    if (field.entry_schema) refs.push(...collectOptionsRefs(field.entry_schema));
  }
  return [...new Set(refs)];
}
